import type { ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '../../ui/Button/Button'
import type { OnboardingApiClient } from './onboarding-api'
import type { ConfirmedOrganizationClinic } from './organization-onboarding-contracts'
import { communicationsSetupCopy } from './communications-setup-presentation'
import { useCommunicationsStatus } from './use-communications-status'

// Mount only for an organization-authorized clinic. Read-only: provisioning
// remains exclusively in the existing, explicitly entered setup flow.
export function CommunicationsSetupStatus({
  tenantUid,
  fallbackStatus,
  apiClient,
  actionLabel = 'Set up phone & texting',
  primary = false,
  providerVerification = false,
  enabled = true,
  render,
}: {
  tenantUid: string
  fallbackStatus?: ConfirmedOrganizationClinic['communicationsStatus']
  apiClient?: OnboardingApiClient
  actionLabel?: string
  primary?: boolean
  providerVerification?: boolean
  enabled?: boolean
  render?: (view: {
    ready: boolean
    number: string
    label: string
    details: ReactNode
    action: ReactNode
  }) => ReactNode
}) {
  const navigate = useNavigate()
  const { user, query, verification, state, verified } =
    useCommunicationsStatus({
      tenantUid,
      fallbackStatus,
      apiClient,
      providerVerification,
      enabled,
    })
  const ready = providerVerification
    ? verified?.status === 'CONNECTED'
    : state?.status === 'READY' || fallbackStatus === 'ACTIVE'
  const label = !providerVerification
    ? ready
      ? 'Active'
      : (state?.status.toLowerCase().replaceAll('_', ' ') ??
        fallbackStatus?.toLowerCase().replaceAll('_', ' ') ??
        'Checking setup')
    : verified
      ? {
          CONNECTED: 'Connected',
          NEEDS_REPAIR: 'Needs repair',
          SETUP_REQUIRED: 'Setup required',
          PROVISIONING: 'Provisioning',
          BLOCKED: 'Blocked',
        }[verified.status]
      : verification.isFetching
        ? 'Verifying'
        : 'Verification unavailable'
  const details = (
    <>
      {verified?.publicNumber ? (
        <p>Twilio number: {verified.publicNumber}</p>
      ) : null}
      {verified?.status === 'NEEDS_REPAIR' ? (
        <p>
          Twilio routing differs from Flio's verified configuration. Review the
          connection before making changes.
        </p>
      ) : null}
      {state && !ready ? <p>{communicationsSetupCopy(state)}</p> : null}
      {query.isError || verification.isError || (query.data && !state) ? (
        <p role="status">
          Communications details are unavailable. The clinic workspace remains
          available.
        </p>
      ) : null}
    </>
  )
  const continuing =
    ['PROVISIONING', 'VALIDATING', 'PARTIAL', 'BLOCKED'].includes(
      state?.status ?? ''
    ) || ['IN_PROGRESS', 'NEEDS_ATTENTION'].includes(fallbackStatus ?? '')
  const action =
    user && enabled && (providerVerification || !ready) ? (
      <Button
        type="button"
        variant={primary ? 'primary' : 'secondary'}
        onClick={() =>
          void navigate(
            `/organization/clinics/${encodeURIComponent(tenantUid)}/communications`
          )
        }
      >
        {ready
          ? 'Manage'
          : verified?.status === 'NEEDS_REPAIR'
            ? 'Repair Twilio connection'
            : render && continuing
              ? 'Continue setup'
              : state?.publicNumber || verified?.publicNumber
                ? 'Manage Twilio connection'
                : actionLabel}
      </Button>
    ) : null
  if (render)
    return render({
      ready,
      number: state
        ? (state.publicNumber ?? 'Not assigned')
        : verified
          ? (verified.publicNumber ?? 'Not assigned')
          : 'Assignment unavailable',
      label: label.charAt(0).toUpperCase() + label.slice(1),
      details,
      action,
    })
  return (
    <div>
      <p>Communications: {label}</p>
      {details}
      {action}
    </div>
  )
}
